import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import type { AxiosError } from "axios";
import axios from "axios";
import { detailedDiff } from "deep-object-diff";
import type { DateTime } from "luxon";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer-continued";
import type { ReactDiffViewerStylesVariables } from "react-diff-viewer-continued/lib/src/styles";
import { isDateTime } from "@cpt/shared";
import { LoadablePage } from "shared/LoadablePage";
import { useOctopusTheme, useThemePalette, withTheme } from "components/Theme";

export type Substantivity = "substantive" | "trivial" | "unknown";

type HistoryChangeDetailViewProps<T extends object> = {
  timestamp: DateTime;
  previousTimestamp: DateTime;
  currentEntity: T | undefined;
  currentEntityError: Error | undefined;
  previousEntity: T | undefined;
  previousEntityError: Error | undefined;
  substantivity: Substantivity;
  projectFields?: (snapshot: T) => object;
};

// FileUsage fields are bumped by the cloud usage-reporting background job and would flood the history view (GPT-109).
const noisyKeySubstrings = ["lastmodified", "created"];
const noisyKeyExactMatches = new Set<string>(["fileusageingb", "fileusagelastcheckedthreshold"]);

const filterNonMeaningfulDiffKeys = (key: string): boolean => {
  const lowerKey = key.toLowerCase();
  if (noisyKeyExactMatches.has(lowerKey)) return false;
  return !noisyKeySubstrings.some((needle) => lowerKey.includes(needle));
};

type SnapshotRecord = Record<string, unknown>;

function isObject(value: unknown): value is SnapshotRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function tryParseJsonString(value: string): unknown {
  const trimmed = value.trim();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      return JSON.parse(trimmed);
    } catch {
      // Not valid JSON — return as-is
    }
  }
  return value;
}

export function stripNoisyFields(snapshot: object): SnapshotRecord {
  return Object.fromEntries(
    Object.entries(snapshot)
      .filter(([key]) => filterNonMeaningfulDiffKeys(key))
      .map(([key, value]): [string, unknown] => {
        // Luxon DateTimes serialise to their noisy internal representation — convert to ISO string instead
        if (isDateTime(value)) return [key, value.toISO()];
        if (Array.isArray(value)) return [key, value.map((v) => (isObject(v) ? stripNoisyFields(v) : v))];
        // FOR JSON PATH returns nvarchar(max) JSON columns as escaped strings — parse and recurse.
        if (typeof value === "string") {
          const parsed = tryParseJsonString(value);
          if (parsed !== value) {
            if (isObject(parsed)) return [key, stripNoisyFields(parsed)];
            if (Array.isArray(parsed)) return [key, parsed.map((v) => (isObject(v) ? stripNoisyFields(v) : v))];
            return [key, parsed];
          }
          return [key, value];
        }
        if (!isObject(value)) return [key, value];
        return [key, stripNoisyFields(value)];
      })
  );
}

function hasLicenseEntityFetchError(entity: object | undefined): boolean {
  return !!entity && "licenseEntityFetchError" in entity && entity.licenseEntityFetchError === true;
}

const isSuppressibleAxiosError = (error: AxiosError) => error.response?.status === 422;

function isSuppressibleError(error: Error | undefined): boolean {
  return !!error && axios.isAxiosError(error) && isSuppressibleAxiosError(error);
}

// Returns "unknown" when classification can't be determined; callers treat unknown as substantive so entries are never silently hidden.
export function classifyHistoryChange<T extends object>(
  previous: T | undefined,
  current: T | undefined,
  previousError: Error | undefined,
  currentError: Error | undefined,
  projectFields?: (snapshot: T) => object
): Substantivity {
  if (isSuppressibleError(previousError) || isSuppressibleError(currentError)) return "unknown";
  if (previousError || currentError) return "unknown";
  if (!previous || !current) return "unknown";
  if (hasLicenseEntityFetchError(previous) || hasLicenseEntityFetchError(current)) return "unknown";

  const projectedPrevious = stripNoisyFields(projectFields ? projectFields(previous) : previous);
  const projectedCurrent = stripNoisyFields(projectFields ? projectFields(current) : current);
  const diff = detailedDiff(projectedPrevious, projectedCurrent);

  const addedKeys = Object.keys(diff.added || {}).filter(filterNonMeaningfulDiffKeys);
  const deletedKeys = Object.keys(diff.deleted || {}).filter(filterNonMeaningfulDiffKeys);
  const updatedKeys = Object.keys(diff.updated || {}).filter(filterNonMeaningfulDiffKeys);

  return addedKeys.length + deletedKeys.length + updatedKeys.length > 0 ? "substantive" : "trivial";
}

export function HistoryChangeDetailView<T extends object>(props: HistoryChangeDetailViewProps<T>) {
  const octopusTheme = useOctopusTheme();
  const [themePalette] = useThemePalette();
  const {
    timestamp,
    previousTimestamp,
    currentEntity,
    currentEntityError,
    previousEntity,
    previousEntityError,
    substantivity,
    projectFields,
  } = props;

  const loading =
    currentEntity === undefined && previousEntity === undefined && !currentEntityError && !previousEntityError;

  const displayEntity = useMemo<SnapshotRecord | undefined>(() => {
    if (!currentEntity) return undefined;
    return stripNoisyFields(projectFields ? projectFields(currentEntity) : currentEntity);
  }, [currentEntity, projectFields]);

  const displayPreviousEntity = useMemo<SnapshotRecord | undefined>(() => {
    if (!previousEntity) return undefined;
    return stripNoisyFields(projectFields ? projectFields(previousEntity) : previousEntity);
  }, [previousEntity, projectFields]);

  // Substantivity is computed once in EntityHistoryView (it drives visibility) and passed down, rather than re-deriving it here.
  const hasMeaningfulChanges = substantivity === "substantive";

  const showLicenseEntityFetchError = useMemo(
    () => hasLicenseEntityFetchError(currentEntity) || hasLicenseEntityFetchError(previousEntity),
    [currentEntity, previousEntity]
  );

  const [displayableError, isSuppressingError] = useMemo(() => {
    const error = currentEntityError || previousEntityError;

    if (!error) {
      return [undefined, false];
    }

    if (axios.isAxiosError(error) && isSuppressibleAxiosError(error)) {
      return [undefined, true];
    }

    return [error, false];
  }, [currentEntityError, previousEntityError]);

  const baseEditorColors: ReactDiffViewerStylesVariables = {
    diffViewerBackground: octopusTheme.primaryBackground,
    diffViewerColor: octopusTheme.primaryText,
    addedBackground: octopusTheme.diffAddedBackground,
    addedColor: octopusTheme.diffAddedColor,
    removedBackground: octopusTheme.diffRemovedBackground,
    removedColor: octopusTheme.diffRemovedColor,
    wordAddedBackground: octopusTheme.diffWordAddedBackground,
    wordRemovedBackground: octopusTheme.diffWordRemovedBackground,
    addedGutterBackground: octopusTheme.diffAddedGutterBackground,
    addedGutterColor: octopusTheme.diffAddedGutterColor,
    removedGutterBackground: octopusTheme.diffRemovedGutterBackground,
    removedGutterColor: octopusTheme.diffRemovedGutterColor,
    gutterBackground: octopusTheme.paper0,
    highlightBackground: octopusTheme.diffHighlight,
    highlightGutterBackground: octopusTheme.diffHighlight,
    codeFoldGutterBackground: octopusTheme.primaryBackground,
    codeFoldBackground: octopusTheme.primaryBackground,
    emptyLineBackground: octopusTheme.primaryBackground,
    gutterColor: octopusTheme.diffGutterColor,
    codeFoldContentColor: octopusTheme.primaryText,
    diffViewerTitleBackground: octopusTheme.primaryBackground,
    diffViewerTitleColor: octopusTheme.primaryText,
  };

  return withTheme((theme) => (
    <LoadablePage loading={loading} error={displayableError}>
      {isSuppressingError && (
        <Typography
          sx={{
            color: theme.secondaryText,
            fontStyle: "italic",
            pl: 2.5,
            py: 1,
          }}
        >
          Unable to load change details. This is likely due to changes in the database schema.
        </Typography>
      )}
      {showLicenseEntityFetchError && (
        <Typography
          sx={{
            color: theme.secondaryText,
            fontStyle: "italic",
            pl: 2.5,
            py: 1,
          }}
        >
          Unable to load complete change details. Some license entity information could not be retrieved.
        </Typography>
      )}
      {!isSuppressingError && !showLicenseEntityFetchError && !hasMeaningfulChanges && (
        <Typography
          sx={{
            color: theme.secondaryText,
            fontStyle: "italic",
            pl: 2.5,
            py: 1,
          }}
        >
          No meaningful changes
        </Typography>
      )}
      {hasMeaningfulChanges && (
        <Box
          sx={{
            "& pre": {
              backgroundColor: "initial",
              padding: "initial",
              margin: "initial",
              wordBreak: "break-word",
              wordWrap: "break-word",
              whiteSpace: "pre-wrap",
              maxWidth: "100%",
            },
            "& .diff-viewer td": {
              maxWidth: "50vw",
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
            },
          }}
        >
          <ReactDiffViewer
            oldValue={JSON.stringify(displayPreviousEntity, null, 2)}
            newValue={JSON.stringify(displayEntity, null, 2)}
            compareMethod={DiffMethod.LINES}
            useDarkTheme={themePalette === "dark"}
            hideLineNumbers={true}
            leftTitle={previousTimestamp.toFormat("dd MMM yyyy, hh:mm:ss a")}
            rightTitle={timestamp.toFormat("dd MMM yyyy, hh:mm:ss a")}
            extraLinesSurroundingDiff={2}
            styles={{
              variables: {
                light: baseEditorColors,
                dark: baseEditorColors,
              },
              wordAdded: {
                color: theme.diffWordAddedColor,
              },
              wordRemoved: {
                color: theme.diffWordRemovedColor,
              },
            }}
          />
        </Box>
      )}
    </LoadablePage>
  ));
}
